// Client-side image helpers.

// Resize an image file before sending it to an AI extractor or uploading.
// Phone photos/screenshots are typically 1–3 MB; downscaling to a 1600px max
// edge drops that to ~200KB and the AI reads them just as well.
// Returns { base64, mimeType, blob } (base64 without the data: prefix;
// blob is the original file when no resize was needed).
export async function downscaleImage(file, maxEdge = 1600, quality = 0.85) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
  // No resize needed and already small enough — return original
  if (scale === 1 && file.size < 1.5 * 1024 * 1024) {
    return { base64: dataUrl.split(',')[1], mimeType: file.type, blob: file };
  }
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  const outDataUrl = canvas.toDataURL('image/jpeg', quality);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  return { base64: outDataUrl.split(',')[1], mimeType: 'image/jpeg', blob };
}

// Wrap a Blob/File so it has a filename and acts like a File for upload paths
// that read `imageFile.name`.
export function blobToFile(blob, originalName) {
  const ext = blob.type === 'image/jpeg' ? 'jpg' : blob.type.split('/')[1] || 'bin';
  const base = (originalName || 'flyer').replace(/\.[^.]+$/, '');
  return new File([blob], `${base}.${ext}`, { type: blob.type });
}

// File-extension lookup for storage uploads.
export const MIME_TO_EXT = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
};
